import type { Project, User } from '@/models'

let API_BASE_URL = import.meta.env.VITE_SERVER_HOST || 'https://localhost:8000'

if (import.meta.env.DEV) {
  API_BASE_URL += `:${import.meta.env.VITE_SERVER_PORT}`
}
API_BASE_URL += '/api/v1'

export interface CreateUserResponse {
  user: User
  memorablePassword: string
  message: string
}

export interface LoginResponse {
  userId: string
  message: string
}

/**
 * Create a new user account
 * Returns user data + memorable password (ONLY TIME IT'S SHOWN!)
 */
export async function createUser(): Promise<CreateUserResponse> {
  const response = await fetch(`${API_BASE_URL}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })

  if (!response.ok) {
    throw new Error('Failed to create user')
  }

  return await response.json()
}

/**
 * Login with memorable password
 * Returns userId for loading profile
 */
export async function loginWithMemorablePassword(
  memorablePassword: string,
): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/users/login/${memorablePassword}`, {
    method: 'GET',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Login failed')
  }

  return await response.json()
}

/**
 * Get user data by ID
 */
export async function getUserById(userId: string): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error('Failed to load user data')
  }

  return await response.json()
}

/**
 * Update user data (projects, liked items)
 */
export async function updateUser(
  userId: string,
  data: {
    projects?: Project[]
    likedResources?: string[]
    likedCases?: string[]
  },
): Promise<{ user: User }> {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error('Failed to update user data')
  }

  return await response.json()
}

/**
 * Delete user account
 */
export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    throw new Error('Failed to delete user')
  }
}
